/**
 * Item status options with display labels, colors, and review flags
 *
 * Status meanings:
 * - process: Awaiting AI processing (auto-handled)
 * - review: AI flagged for human review
 * - ready: Processed and ready for assignment
 * - assigned: Has ItemInstance linked to an issue
 * - used: URL reappeared after being in a prior issue (needs review)
 * - error: AI processing failed (needs review/retry)
 * - archived: Temporarily archived
 * - duplicate: Different URL, same content detected (needs review)
 * - blocked: Permanently blocked from processing
 * - missing-content: Needs full content capture via browser extension (needs review)
 */
export const ITEM_STATUSES = [
  {
    value: 'process',
    label: 'Processing',
    description: 'Awaiting AI analysis',
    color: 'bg-sky-50 text-sky-700 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-800/50',
    needsReview: false,
  },
  {
    value: 'review',
    label: 'Needs Review',
    description: 'AI flagged for human review',
    color: 'bg-yellow-50 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800/50',
    needsReview: true,
  },
  {
    value: 'ready',
    label: 'Ready',
    description: 'Ready to assign to an issue',
    color: 'bg-green-50 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800/50',
    needsReview: false,
  },
  {
    value: 'assigned',
    label: 'Assigned',
    description: 'Assigned to a newsletter issue',
    color: 'bg-blue-600 text-white border-blue-700',
    needsReview: false,
  },
  {
    value: 'used',
    label: 'Previously Used',
    description: 'URL reappeared after being in a prior issue',
    color: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800/50',
    needsReview: true,
  },
  {
    value: 'error',
    label: 'Error',
    description: 'AI processing failed',
    color: 'bg-red-500 text-white border-red-600',
    needsReview: true,
  },
  {
    value: 'archived',
    label: 'Archived',
    description: 'Temporarily archived',
    color: 'bg-gray-50 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
    needsReview: false,
  },
  {
    value: 'duplicate',
    label: 'Duplicate',
    description: 'Different URL, same content detected',
    color: 'bg-orange-50 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800/50',
    needsReview: true,
  },
  {
    value: 'blocked',
    label: 'Blocked',
    description: 'Permanently blocked',
    color: 'bg-gray-800 text-white border-gray-900',
    needsReview: false,
  },
  {
    value: 'missing-content',
    label: 'Missing Content',
    description: 'Needs full content capture via browser extension',
    color: 'bg-purple-50 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800/50',
    needsReview: true,
  },
];

/**
 * Filter options for items
 */
export const ITEM_FILTER_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'review', label: 'Needs Review' },
  { value: 'url', label: 'URL' },
  { value: 'issueNumber', label: 'Issue Number' },
];

/**
 * Get status option by value
 * @param {string} status - The status value
 * @returns {Object|undefined} The status option object
 */
export const getStatusOption = (status) => {
  return ITEM_STATUSES.find((opt) => opt.value === status);
};

/**
 * Get status color classes by value
 * @param {string} status - The status value
 * @returns {string} The color classes or default
 */
export const getStatusColor = (status) => {
  const option = getStatusOption(status);
  return option?.color || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

/**
 * Get status label by value
 * @param {string} status - The status value
 * @returns {string} The label or the status value itself
 */
export const getStatusLabel = (status) => {
  const option = getStatusOption(status);
  return option?.label || status;
};

/**
 * Get status description by value
 * @param {string} status - The status value
 * @returns {string} The description or empty string
 */
export const getStatusDescription = (status) => {
  const option = getStatusOption(status);
  return option?.description || '';
};

/**
 * Check if a status needs human review
 * @param {string} status - The status value
 * @returns {boolean} True if status needs review
 */
export const statusNeedsReview = (status) => {
  const option = getStatusOption(status);
  return option?.needsReview || false;
};

/**
 * Get all statuses that need human review
 * @returns {string[]} Array of status values that need review
 */
export const getReviewStatuses = () => {
  return ITEM_STATUSES.filter((opt) => opt.needsReview).map((opt) => opt.value);
};

/**
 * Item category options with display labels and colors
 *
 * Categories:
 * - news: Informational content, announcements
 * - tutorial: Learning content, how-to guides
 * - reads: Long-form articles, thought pieces
 * - multimedia: Videos, podcasts, images
 * - aws: AWS-specific content
 * - social: Social media posts, threads
 * - tools: Developer tools, libraries
 * - security: Security-related content
 * - events: Conferences, meetups, webinars
 */
export const ITEM_CATEGORIES = [
  {
    value: 'news',
    label: 'News',
    color: 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/50',
    hoverColor: 'hover:bg-blue-100 dark:hover:bg-blue-900/60',
  },
  {
    value: 'tutorial',
    label: 'Tutorial',
    color: 'bg-green-50 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800/50',
    hoverColor: 'hover:bg-green-100 dark:hover:bg-green-900/60',
  },
  {
    value: 'reads',
    label: 'Reads',
    color: 'bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800/50',
    hoverColor: 'hover:bg-purple-100 dark:hover:bg-purple-900/60',
  },
  {
    value: 'multimedia',
    label: 'Multimedia',
    color: 'bg-pink-50 text-pink-700 border-pink-300 dark:bg-pink-900/40 dark:text-pink-200 dark:border-pink-800/50',
    hoverColor: 'hover:bg-pink-100 dark:hover:bg-pink-900/60',
  },
  {
    value: 'aws',
    label: 'AWS',
    color: 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800/50',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/60',
  },
  {
    value: 'social',
    label: 'Social',
    color: 'bg-cyan-50 text-cyan-700 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-200 dark:border-cyan-800/50',
    hoverColor: 'hover:bg-cyan-100 dark:hover:bg-cyan-900/60',
  },
  {
    value: 'tools',
    label: 'Tools',
    color: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600',
    hoverColor: 'hover:bg-slate-200 dark:hover:bg-slate-700',
  },
  {
    value: 'security',
    label: 'Security',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800/50',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/60',
  },
  {
    value: 'events',
    label: 'Events',
    color: 'bg-red-50 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800/50',
    hoverColor: 'hover:bg-red-100 dark:hover:bg-red-900/60',
  },
];

/**
 * Get category option by value
 * @param {string} category - The category value
 * @returns {Object|undefined} The category option object
 */
export const getCategoryOption = (category) => {
  return ITEM_CATEGORIES.find((opt) => opt.value === category);
};

/**
 * Get category color classes by value
 * @param {string} category - The category value
 * @returns {string} The color classes or default
 */
export const getCategoryColor = (category) => {
  const option = getCategoryOption(category);
  return option?.color || 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600';
};

/**
 * Get category hover color class by value
 * @param {string} category - The category value
 * @returns {string} The hover color class or default
 */
export const getCategoryHoverColor = (category) => {
  const option = getCategoryOption(category);
  return option?.hoverColor || 'hover:bg-gray-200 dark:hover:bg-gray-700';
};

/**
 * Get category label by value
 * @param {string} category - The category value
 * @returns {string} The label or the category value itself
 */
export const getCategoryLabel = (category) => {
  const option = getCategoryOption(category);
  return option?.label || category || 'No Category';
};

/**
 * Instance workflow flags with display labels, colors, and styling
 *
 * Flag meanings:
 * - new: Auto-assigned when instance is created (default)
 * - review: Needs attention/review
 * - followup: Come back to this later
 */
export const INSTANCE_FLAGS = [
  {
    value: 'new',
    label: 'New',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/40',
    borderColor: 'border-green-300 dark:border-green-800/50',
    hoverColor: 'hover:bg-green-200 dark:hover:bg-green-900/60',
  },
  {
    value: 'review',
    label: 'Review',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/40',
    borderColor: 'border-orange-300 dark:border-orange-800/50',
    hoverColor: 'hover:bg-orange-200 dark:hover:bg-orange-900/60',
  },
  {
    value: 'followup',
    label: 'Follow Up',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/40',
    borderColor: 'border-purple-300 dark:border-purple-800/50',
    hoverColor: 'hover:bg-purple-200 dark:hover:bg-purple-900/60',
  },
];

/**
 * Get flag option by value
 * @param {string} flag - The flag value
 * @returns {Object|undefined} The flag option object
 */
export const getFlagOption = (flag) => {
  return INSTANCE_FLAGS.find((opt) => opt.value === flag);
};

/**
 * Get flag color classes by value
 * @param {string} flag - The flag value
 * @returns {string} The color classes or default
 */
export const getFlagColor = (flag) => {
  const option = getFlagOption(flag);
  return option?.color || 'text-gray-400 dark:text-gray-500';
};

/**
 * Get flag background color class by value
 * @param {string} flag - The flag value
 * @returns {string} The background color class or default
 */
export const getFlagBgColor = (flag) => {
  const option = getFlagOption(flag);
  return option?.bgColor || 'bg-gray-100 dark:bg-gray-700';
};

/**
 * Get flag label by value
 * @param {string} flag - The flag value
 * @returns {string} The label or empty string
 */
export const getFlagLabel = (flag) => {
  const option = getFlagOption(flag);
  return option?.label || '';
};
